import {
  Badge,
  BodyText,
  Button,
  Heading,
  Icon,
  IconSet,
  Layout,
  ModalBody,
} from '@postscript/components';
import Player from '@vimeo/player';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { useShopperModals } from 'components/shopper/components/modals/ShopperModalRouter';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useUser } from 'controllers/contexts/user';
import { useShopperStatus } from 'hooks/useShopperStatus';
import { useProgressiveOnboarding } from 'providers/ProgressiveOnboarding/ProgressiveOnboardingProvider';
import { useHistory } from 'react-router-dom';

import { Stack } from 'components/layout';
import { Flex, Media } from 'components/shopper/styles/styles';

import { PROGRESSIVE_ONBOARDING_SHOPPER } from 'components/admin/utils/feature-flags';
import { SHOPPER_CONFIG_BASE_PATH } from 'components/shopper/constants';
import { SHOPPER_FEATURE_KEY } from 'providers/ProgressiveOnboarding/constants/constants';

import {
  COMMON_EVENT_NAMES,
  logButtonClickEvent,
  logEvent,
  MODAL_TYPES,
  PRODUCT_AREAS,
} from 'utils/events';

const Container = styled(ModalBody)`
  background-color: var(--gray-9);
  display: flex;
`;

const CloseButton = styled(Button)`
  position: absolute;
  right: 1rem;
  top: 1rem;
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: var(--spacing-6);
  gap: 24px;
`;

const VideoContent = styled(Content)`
  position: relative;
`;

const GreenBackground = styled.div`
  background: radial-gradient(
    circle at center,
    rgba(0, 255, 115, 0.6) 10%,
    rgba(0, 255, 115, 0) 70%
  );
  position: absolute;
  inset: -100px;
  opacity: 0.5;
  height: 50%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const VideoWrapper = styled.div`
  position: relative;
  padding: 56.25% 0 0 0;
`;

const Iframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 12px;
`;

const ListItem = styled.li`
  list-style: none;

  display: flex;
  gap: var(--spacing-3);
  padding: var(--spacing-4) var(--spacing-3) var(--spacing-4) var(--spacing-2);

  &:not(:last-child) {
    border-bottom: 1px solid var(--gray-6);
  }

  svg {
    color: var(--blue-2);
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;

  button {
    flex: 1;
  }
`;

const ShopperLearnMoreModal = () => {
  const history = useHistory();
  const { user } = useUser();
  const { hasLabsFlag } = usePSLabs();
  const { hideModal, showDemoModal, showShopperModal } = useShopperModals();
  const { availableGuides, setActiveGuide } = useProgressiveOnboarding();
  const { isBrandCenterReady } = useShopperStatus();
  const iframeRef = useRef(null);

  const heading = 'Meet Shopper';
  const source = 'shopper learn more modal';

  async function trackVimeoPlayerEvents(iframe: HTMLIFrameElement) {
    const player = new Player(iframe);

    player.on('play', () =>
      logEvent(COMMON_EVENT_NAMES.BUTTON_CLICKED, {
        action: 'play Meet Shopper video',
        modal_heading: heading,
        modal_type: MODAL_TYPES.SHOPPER,
        product_area: PRODUCT_AREAS.SHOPPER,
        shop_id: user.shop_id,
        source,
      }),
    );
  }

  useEffect(() => {
    if (!iframeRef.current) return;
    trackVimeoPlayerEvents(iframeRef.current);
  }, []);

  const onClickGetStarted = (event: React.MouseEvent<HTMLButtonElement>) => {
    hideModal();

    logButtonClickEvent(event, {
      modal_heading: heading,
      modal_type: MODAL_TYPES.SHOPPER,
      product_area: PRODUCT_AREAS.SHOPPER,
      shop_id: user.shop_id,
      source,
    });

    const shopperGuide = availableGuides.find(
      (guide) => guide.key === SHOPPER_FEATURE_KEY,
    );
    if (shopperGuide) {
      setActiveGuide(shopperGuide);
    }
  };

  const onClickStartTrial = (event: React.MouseEvent<HTMLButtonElement>) => {
    hideModal();

    logButtonClickEvent(event, {
      modal_heading: heading,
      modal_type: MODAL_TYPES.SHOPPER,
      product_area: PRODUCT_AREAS.SHOPPER,
      shop_id: user.shop_id,
      source,
    });

    showShopperModal();
  };

  const onClickTryDemo = (event: React.MouseEvent<HTMLButtonElement>) => {
    hideModal();

    logButtonClickEvent(event, {
      modal_heading: heading,
      modal_type: MODAL_TYPES.SHOPPER,
      product_area: PRODUCT_AREAS.SHOPPER,
      shop_id: user.shop_id,
      source,
    });

    // Send the user to the Brand Center
    history.push(SHOPPER_CONFIG_BASE_PATH);

    // If it's already setup, then immediately show the demo
    if (isBrandCenterReady) {
      showDemoModal();
    }
  };

  const handleClose = () => {
    logEvent(COMMON_EVENT_NAMES.BUTTON_CLICKED, {
      modal_heading: heading,
      modal_type: MODAL_TYPES.SHOPPER,
      product_area: PRODUCT_AREAS.SHOPPER,
      shop_id: user.shop_id,
      source,
      text: 'Close',
    });

    hideModal();
  };

  return (
    <Container data-theme="dark">
      <CloseButton variant="plain" onClick={handleClose} icon={IconSet.Close} />
      <VideoContent>
        <GreenBackground />
        <Media>
          <VideoWrapper>
            <Iframe
              src="https://fast.wistia.net/embed/iframe/z52w46376e?videoFoam=true"
              allowFullScreen
              title="Meet Shopper"
              ref={iframeRef}
            />
          </VideoWrapper>
          <Flex gap={6} justifyCenter>
            Watch the launch video
          </Flex>
        </Media>
      </VideoContent>
      <Content>
        <Header>
          <Layout
            gap="var(--spacing-2)"
            style={{ marginBottom: 'var(--spacing-2)' }}
          >
            <Heading
              as="h2"
              size="large"
              color="white"
              data-testid="shopper-promo-header"
            >
              {heading}
            </Heading>
            <Badge color="var(--white)" size="small" variant="info">
              Beta
            </Badge>
          </Layout>
          <BodyText size="large">
            An AI SMS assistant that engages shoppers, answers questions, and
            boosts sales with personalized chats.
          </BodyText>
        </Header>

        <ul>
          <ListItem>
            <Icon component={IconSet.Book} />
            <BodyText size="medium" color="var(--text-color)">
              <strong>Learn directly from your customers</strong> how to improve
              your products and marketing.
            </BodyText>
          </ListItem>

          <ListItem>
            <Icon component={IconSet.CheckCircle} />
            <BodyText size="medium" color="var(--text-color)">
              <strong>Reduce support tickets</strong> with custom AI responses,
              keeping customers informed and engaged.
            </BodyText>
          </ListItem>

          <ListItem>
            <Icon component={IconSet.TrendingUp} />
            <BodyText size="medium" color="var(--text-color)">
              <strong>Gain valuable customer insights</strong> to refine
              marketing and increase revenue effortlessly.
            </BodyText>
          </ListItem>

          <ListItem>
            <Stack gap="var(--spacing-1)">
              <Heading size="x-small">Try Shopper on us!</Heading>
              <BodyText size="medium">
                No AI reply charges for <strong>30 days</strong>, plus platform
                fee waived during beta for Professional & Enterprise plans.
              </BodyText>
            </Stack>
          </ListItem>
        </ul>

        <Actions>
          {hasLabsFlag(PROGRESSIVE_ONBOARDING_SHOPPER) ? (
            <Button onClick={onClickGetStarted}>Get Started</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={onClickStartTrial}>
                Start a Trial
              </Button>
              <Button onClick={onClickTryDemo}>Try a Demo</Button>
            </>
          )}
        </Actions>
      </Content>
    </Container>
  );
};

export default ShopperLearnMoreModal;
